import React, { useState } from 'react';
import BadgeAvatar from '../components/BadgeAvatar';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const CreateTest = ({ user, logout }) => {
  const { difficulty } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [useAI, setUseAI] = useState(false);
  
  const [formData, setFormData] = useState({
    case_scenario: '',
    time_limit: 540,
    tcm_topic: '',
    case_info: '',  // New field for detailed case information
    model: 'gpt-4o-mini',
    is_featured: false  // Featured test checkbox
  });

  const [questions, setQuestions] = useState([
    { section: 'diagnosis', question: 'What is the primary TCM diagnosis?', options: ['', '', '', ''], correct_answer: 0 },
    { section: 'treatment_principle', question: 'What is the primary treatment principle?', options: ['', '', '', ''], correct_answer: 0 },
    { section: 'differential', question: 'Best differential diagnosis?', options: ['', '', '', ''], correct_answer: 0 },
    { section: 'points', question: 'Which point combination is most appropriate?', options: ['', '', '', ''], correct_answer: 0 },
    { section: 'nutrition', question: 'Best nutritional recommendation?', options: ['', '', '', ''], correct_answer: 0 },
    { section: 'daily_activities', question: 'Most important lifestyle advice?', options: ['', '', '', ''], correct_answer: 0 }
  ]);

  const aiModels = [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast)' },
    { value: 'gpt-4o', label: 'GPT-4o (Balanced)' },
    { value: 'gpt-5', label: 'GPT-5 (Advanced)' },
    { value: 'claude-3-7-sonnet', label: 'Claude 3.7 Sonnet' },
    { value: 'claude-4-sonnet', label: 'Claude 4 Sonnet' }
  ];

  const handleGenerateAI = async () => {
    if (!formData.case_info && !formData.tcm_topic) {
      toast.error('Please provide case information or TCM topic for AI generation');
      return;
    }

    setLoading(true);
    try {
      toast('Generating test with AI... This may take 30-60 seconds');
      const response = await api.post('/admin/tests/generate', {
        difficulty,
        tcm_topic: formData.tcm_topic,
        case_info: formData.case_info,
        model: formData.model
      });

      // Populate the form with AI-generated content
      if (response.data.case_scenario) {
        setFormData({ ...formData, case_scenario: response.data.case_scenario });
      }
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
      }
      
      toast.success('Test generated! Review and edit as needed, then save.');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate test');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options[oIndex] = value;
    setQuestions(newQuestions);
  };

  const handleCorrectAnswerChange = (qIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].correct_answer = parseInt(value);
    setQuestions(newQuestions);
  };

  const handleSave = async () => {
    if (!formData.case_scenario || questions.length === 0) {
      toast.error('Please provide case scenario and questions');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/admin/tests/save', {
        difficulty,
        case_scenario: formData.case_scenario,
        questions,
        time_limit: formData.time_limit,
        is_featured: formData.is_featured
      });
      
      toast.success('Test saved successfully! View it in the Admin panel.');
      
      // Redirect to admin after short delay
      setTimeout(() => {
        window.location.href = '/admin';
      }, 2000);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to save test');
    } finally {
      setLoading(false);
    }
  };

  const sectionLabels = {
    diagnosis: 'Diagnosis',
    treatment_principle: 'Treatment Principle',
    differential: 'Differential Diagnosis',
    points: 'Acupuncture Points',
    nutrition: 'Nutrition',
    daily_activities: 'Daily Life Advice'
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      <header className='bg-white shadow-sm border-b border-gray-200'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <Link to='/dashboard' className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center'>
                <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
                </svg>
              </div>
              <span className='text-xl font-bold text-gray-900' style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
            </Link>
            <div className='flex items-center space-x-4'>
              <Link to={`/profile/${user.id}`}>
                <img src={user.picture || 'https://via.placeholder.com/40'} alt={user.name} className='w-10 h-10 rounded-full border-2 border-teal-500' />
              </Link>
              <Button onClick={logout} variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <div className='container mx-auto px-4 py-8 max-w-5xl'>
        <div className='bg-white rounded-3xl p-8 shadow-lg'>
          <div className='mb-8'>
            <div className='flex items-center justify-between'>
              <div>
                <h1 className='text-4xl font-bold text-gray-900 mb-2' style={{ fontFamily: 'Playfair Display, serif' }}>
                  Create {difficulty === 'simple' ? 'Simple' : 'Complex'} Test
                </h1>
                <p className='text-gray-600'>Create a new competency test for students</p>
              </div>
              <Badge className={`${difficulty === 'simple' ? 'difficulty-simple' : 'difficulty-complex'} border-0 text-lg px-6 py-2`}>
                {difficulty === 'simple' ? '📘 Simple' : '📕 Complex'}
              </Badge>
            </div>
          </div>

          <div className='space-y-6'>
            {/* AI Toggle */}
            <div className='bg-purple-50 border border-purple-200 rounded-xl p-4'>
              <label className='flex items-center space-x-3 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  className='w-5 h-5 text-purple-600'
                />
                <span className='font-medium text-purple-900'>Use AI to help generate test content</span>
              </label>
            </div>

            {useAI && (
              <div className='bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border-2 border-teal-200 space-y-4'>
                <h3 className='font-bold text-gray-900'>AI Test Generator</h3>
                
                <div>
                  <Label>Case Information (Optional)</Label>
                  <Textarea
                    value={formData.case_info}
                    onChange={(e) => setFormData({ ...formData, case_info: e.target.value })}
                    placeholder={`Provide detailed patient information that AI will transform into a ${difficulty === 'simple' ? '5-sentence case study' : '3-4 paragraph case study'}. Include: patient demographics, symptoms, tongue, pulse, medical history, etc.`}
                    className='border-gray-300 rounded-xl min-h-[120px]'
                  />
                  <p className='text-xs text-gray-600 mt-1'>
                    AI will create a {difficulty === 'simple' ? '5-sentence' : '3-4 paragraph'} case study from this information
                  </p>
                </div>
                
                <div>
                  <Label>OR TCM Topic (Optional)</Label>
                  <Input
                    value={formData.tcm_topic}
                    onChange={(e) => setFormData({ ...formData, tcm_topic: e.target.value })}
                    placeholder='e.g., Liver Qi Stagnation, Kidney Yang Deficiency'
                    className='border-gray-300 rounded-xl'
                  />
                  <p className='text-xs text-gray-600 mt-1'>
                    Provide either Case Information OR TCM Topic (or both)
                  </p>
                </div>
                
                <div>
                  <Label>AI Model</Label>
                  <Select value={formData.model} onValueChange={(value) => setFormData({ ...formData, model: value })}>
                    <SelectTrigger className='w-full border-gray-300 rounded-xl'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {aiModels.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleGenerateAI}
                  disabled={loading}
                  className='w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-full'
                >
                  {loading ? 'Generating...' : 'Generate with AI'}
                </Button>
              </div>
            )}

            {/* Case Scenario */}
            <div>
              <Label>Case Scenario *</Label>
              <Textarea
                value={formData.case_scenario}
                onChange={(e) => setFormData({ ...formData, case_scenario: e.target.value })}
                placeholder='Describe the patient case scenario including age, gender, chief complaint, symptoms, tongue, and pulse...'
                className='border-gray-300 rounded-xl min-h-[120px]'
              />
            </div>

            {/* Time Limit */}
            <div>
              <Label>Time Limit (seconds) *</Label>
              <Input
                type='number'
                value={formData.time_limit}
                onChange={(e) => setFormData({ ...formData, time_limit: parseInt(e.target.value) })}
                className='border-gray-300 rounded-xl'
                min='60'
              />
              <p className='text-xs text-gray-600 mt-1'>Default: 540 seconds (9 minutes) = 90 seconds per question</p>
            </div>

            {/* Featured Test Checkbox */}
            <div className='bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4'>
              <label className='flex items-start space-x-3 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className='w-5 h-5 text-yellow-600 mt-1'
                />
                <div>
                  <span className='font-bold text-yellow-900'>⭐ Make this the Featured Test</span>
                  <p className='text-sm text-yellow-800 mt-1'>
                    Featured tests are the default tests shown to students. Only ONE {difficulty} test can be featured at a time.
                  </p>
                </div>
              </label>
            </div>

            {/* Questions */}
            <div className='space-y-6 pt-6 border-t border-gray-200'>
              <h3 className='text-xl font-bold text-gray-900'>Test Questions</h3>
              {questions.map((q, qIndex) => (
                <div key={q.section} className='bg-gray-50 rounded-xl p-6'>
                  <h4 className='font-semibold text-gray-900 mb-4'>{qIndex + 1}. {sectionLabels[q.section]}</h4>
                  <div className='space-y-3'>
                    {q.options.map((option, oIndex) => (
                      <div key={oIndex} className='flex items-center space-x-3'>
                        <input
                          type='radio'
                          name={`correct-${qIndex}`}
                          checked={q.correct_answer === oIndex}
                          onChange={() => handleCorrectAnswerChange(qIndex, oIndex)}
                          className='w-4 h-4 text-teal-600'
                        />
                        <Input
                          value={option}
                          onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                          placeholder={`Option ${oIndex + 1}`}
                          className='flex-1 border-gray-300 rounded-lg'
                        />
                        {q.correct_answer === oIndex && (
                          <span className='text-xs text-green-600 font-medium'>Correct</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className='flex justify-between pt-6 border-t border-gray-200'>
              <Link to='/create'>
                <Button variant='outline' className='rounded-full px-8'>Cancel</Button>
              </Link>
              <Button
                onClick={handleSave}
                disabled={loading}
                className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8'
              >
                {loading ? 'Saving...' : 'Save Test'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTest;
